import { matchQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { useOptionalActivityTracker } from "src/activity/ActivityProvider";
import { useTRPC } from "src/api/trpc";

/** Current user, their organizations (sandboxes) and the active one. */
export const useSession = () => {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const activity = useOptionalActivityTracker();
	const me = useQuery(trpc.v1.session.me.queryOptions());
	const signOutMutation = useMutation(trpc.v1.session.signOut.mutationOptions());
	const switchMutation = useMutation(trpc.v1.session.switchOrganization.mutationOptions());

	/**
	 * Drops every cached query except `me`, then refetches `me`. `queryClient.clear()` is not enough:
	 * it empties the cache without telling mounted observers, so the old session stays on screen.
	 */
	const restartSession = useCallback(async () => {
		const meKey = trpc.v1.session.me.queryKey();
		await queryClient.cancelQueries();
		queryClient.removeQueries({ predicate: query => !matchQuery({ queryKey: meKey, exact: true }, query) });
		await queryClient.resetQueries({ queryKey: meKey, exact: true });
	}, [queryClient, trpc]);

	const signOut = useCallback(async () => {
		// Activity tracked so far belongs to this session; deliver or drop it before the cookie goes.
		await activity?.settle();
		await signOutMutation.mutateAsync();
		// Everything cached so far belongs to the signed-in user; `me` comes back signed out.
		await restartSession();
	}, [activity, restartSession, signOutMutation]);

	const switchOrganization = useCallback(
		async (organizationId: string) => {
			// The API stamps events with the organization they arrive under, so nothing from this one may
			// be sent after the switch.
			await activity?.settle();
			await switchMutation.mutateAsync({ organizationId });
			// Every cached query was scoped to the previous sandbox.
			await restartSession();
		},
		[activity, restartSession, switchMutation]
	);

	return {
		isLoading: me.isLoading,
		user: me.data?.user ?? null,
		organizations: me.data?.organizations ?? [],
		activeOrganization: me.data?.activeOrganization ?? null,
		/**
		 * Whether the active tenant is a Digital Realty partner organization. Only partners see an
		 * organization (name, members); a direct prospect's or customer's workspace is never called one.
		 */
		isPartner: me.data?.activeOrganization?.kind === "partner",
		signInEnabled: me.data?.signInEnabled ?? false,
		devLoginEnabled: me.data?.devLoginEnabled ?? false,
		signInLinkMode: me.data?.signInLinkMode ?? null,
		signOut,
		switchOrganization
	};
};
